import { useMemo } from 'react';
import { css } from '@emotion/react';
import { LocationFeatureCreditDetails } from '@weave/schema-gen-ts/dist/schemas/platform-location-feature/v1/location_features.pb';
import { Feature } from '@weave/schema-gen-ts/dist/shared/feature/location_feature.pb';
import { CustomerLifecycleManagementOrg } from '@wetags/customer-lifecycle-management';
import { useTranslation } from '@frontend/i18n';
import { useAppScopeStore, useScopedAppFlagStore } from '@frontend/scope';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { InlineNotification, Text, TextLink } from '@frontend/design-system';
import { getAiReceptionistCreditStatus, LocationCreditStatus } from '../helpers/ai-receptionist-credit-utils';

interface AiReceptionistCreditNotificationsProps {
  locationCreditDetails: Record<string, LocationFeatureCreditDetails> | undefined;
  locationIds: string[];
}

/**
 * Renders error InlineNotifications for AI Receptionist credit/subscription issues.
 * - "Out of credits" error when consumedCredits >= totalCredits (for subscribed locations)
 * - "Not subscribed" error when the TRUELARK_AI_VOICE flag is not ACTIVE
 *
 * Only renders when the "air-credit-enforcement" wetag is enabled for the org.
 */
export function AiReceptionistCreditNotifications({
  locationCreditDetails,
  locationIds,
}: AiReceptionistCreditNotificationsProps) {
  const { getScopeName, isSingleTypeScope } = useAppScopeStore();
  const { getCustomizationFlagLocationMap } = useScopedAppFlagStore();
  const [isCreditEnforcementEnabled] = CustomerLifecycleManagementOrg.useWetag('air-credit-enforcement');

  const creditStatus = useMemo(() => {
    if (!isCreditEnforcementEnabled || !locationCreditDetails || !locationIds.length) return undefined;

    // TODO: Milind - Update the CF logic when WEAVE_VOICE_AIR credit consumption is ready
    const aiVoiceFlagLocationMap = getCustomizationFlagLocationMap(Feature.TRUELARK_AI_VOICE);

    return getAiReceptionistCreditStatus({
      locationCreditDetails,
      locationIds,
      getScopeName,
      aiVoiceFlagLocationMap,
    });
  }, [isCreditEnforcementEnabled, locationCreditDetails, locationIds, getScopeName, getCustomizationFlagLocationMap]);

  if (!creditStatus) return null;

  const { outOfCreditsLocations, notSubscribedLocations } = creditStatus;
  const hasOutOfCredits = outOfCreditsLocations.length > 0;
  const hasNotSubscribed = notSubscribedLocations.length > 0;

  if (!hasOutOfCredits && !hasNotSubscribed) return null;

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: var(--fabric-space-gap-sm);
      `}
    >
      {hasOutOfCredits && <OutOfCreditsNotification locations={outOfCreditsLocations} isUnify={!isSingleTypeScope} />}
      {hasNotSubscribed && <NotSubscribedNotification locations={notSubscribedLocations} />}
    </div>
  );
}

function OutOfCreditsNotification({ locations, isUnify }: { locations: LocationCreditStatus[]; isUnify: boolean }) {
  const { t } = useTranslation('phone');
  const { navigate: settingsNavigate } = useSettingsNavigate();

  // For unify (multi-location) setups, always show the location name.
  const showLocationName = isUnify || locations.length > 1;

  return (
    <InlineNotification status='error' size='small'>
      <Text size='small'>
        {showLocationName ? (
          <>
            <LocationLabel locations={locations} />{' '}
            {locations.length === 1
              ? t('is out of credits. AI Receptionist is skipped until you obtain more credits.')
              : t('are out of credits. AI Receptionist is skipped until you obtain more credits.')}
          </>
        ) : (
          t('Out of credits. AI Receptionist is skipped until you obtain more credits.')
        )}{' '}
        <TextLink
          size='small'
          onClick={() => settingsNavigate({ to: '/subscriptions/feature/:id', params: { id: 'ai-receptionist' } })}
          trackingId='call-route-ai-receptionist-upgrade-link'
        >
          {t('Upgrade')}
        </TextLink>
      </Text>
    </InlineNotification>
  );
}

function NotSubscribedNotification({ locations }: { locations: LocationCreditStatus[] }) {
  const { t } = useTranslation('phone');

  return (
    <InlineNotification status='error' size='small'>
      <Text size='small'>
        <LocationLabel locations={locations} />{' '}
        {locations.length === 1
          ? t(
              "isn't subscribed to AI Receptionist. Until you activate AI Receptionist on this location, callers will automatically be routed to your fallback option."
            )
          : t(
              "aren't subscribed to AI Receptionist. Until you activate AI Receptionist on these locations, callers will automatically be routed to your fallback option."
            )}
      </Text>
    </InlineNotification>
  );
}

/**
 * Renders the location name(s) in bold.
 * Single: "Happy Smiles Denver"
 * Multi: "Happy Smiles Denver + 2 locations"
 */
function LocationLabel({ locations }: { locations: LocationCreditStatus[] }) {
  const { t } = useTranslation('phone');
  const firstName = locations[0].locationName;
  const otherCount = locations.length - 1;

  if (otherCount === 0) {
    return (
      <Text as='span' size='small' weight='bold'>
        {firstName}
      </Text>
    );
  }

  const otherLabel = t('{{count}} locations', { count: otherCount });

  return (
    <Text as='span' size='small' weight='bold'>
      {firstName} + {otherLabel}
    </Text>
  );
}
